import { _isNotEmpty, _notNil } from '@/littledash';
import type { SendCapabilityReport } from '@/model/Agent.model';
import type { FC } from 'react';
import { ratioText } from '@/components/Agents/SendValidator/utils/SendRatio';
import { plural } from '@/components/Agents/SendValidator/utils/SendText';
import { DEFINITION_SOURCE_TEXT, ROUTE_TEXT } from '@/components/Agents/SendValidator/utils/SendTiers';
import styles from './SendResults.module.scss';

interface SendCapabilityLinesProps {
  capability: SendCapabilityReport;
  testId?: string;
}

/** Each count with its words, joined as one line, leaving out none, so the parts can be read against the whole. */
const partsOf = <Key extends string>(counts: Record<Key, number>, text: Record<Key, string>): string =>
  (Object.keys(text) as Array<Key>).map((key) => `${counts[key]} ${text[key]}`).join(' · ');

/**
 * What this build can check, beside what the run did: facts about the build, the same on every upload. Each
 * population has its own denominator and none is summed into another: the published rules (by planned route,
 * and by where each check comes from), the Library encodings outside them as a bonus line, our own checks
 * with Tier 8 apart, and the FDA validator rules out of the FDA's own total.
 */
const SendCapabilityLines: FC<SendCapabilityLinesProps> = ({
  capability,
  testId = 'invivo-send-validator__capability',
}) => {
  const { register, encodings_outside_register: outside, in_house_checks: inHouse, fda_validator: fda } = capability;
  const tierEight = inHouse.by_tier.find((tier) => tier.tier === 'tier_8')?.rules ?? 0;
  const notRun = register.awaiting_implementation.length + register.inputs_profile_lacks.length;
  return (
    <ul className={styles.capabilityLines} data-test-component="SendCapabilityLines" data-testid={testId}>
      {_notNil(register.by_route) && (
        <li data-test-element="capability-route">
          <span className="fw6">{register.rules} published rules, by the route planned for each:</span>{' '}
          {partsOf(register.by_route, ROUTE_TEXT)}
        </li>
      )}
      {_notNil(register.by_definition_source) && (
        <li data-test-element="capability-definition-source">
          <span className="fw6">The same {register.rules}, by where each check comes from:</span>{' '}
          {partsOf(register.by_definition_source, DEFINITION_SOURCE_TEXT)}
        </li>
      )}
      <li data-test-element="capability-not-run">
        This build doesn&apos;t run {plural(notRun, 'published rule', 'published rules')} on any upload:{' '}
        {register.awaiting_implementation.length} waiting on a later build, and {register.inputs_profile_lacks.length}{' '}
        that read an input a CSV upload never carries.
        {_isNotEmpty(register.excluded_by_use_case) &&
          ` ${plural(register.excluded_by_use_case.length, 'rule', 'rules')} CDISC scopes away from nonclinical data read not applicable.`}
      </li>
      <li data-test-element="capability-review">
        {register.review.pending} of {register.rules} published rules&apos; readings are awaiting expert review.
      </li>
      <li data-test-element="capability-outside-register">
        <span className="fw6">Besides those, never added to them:</span> {outside.rules} Library encodings outside the
        published rules.
      </li>
      <li data-test-element="capability-in-house">
        <span className="fw6">Our own checks:</span> {inHouse.checks}, against no published total. Tier 8 (data quality)
        is counted here alone: {tierEight}.
      </li>
      <li data-test-element="capability-fda">
        <span className="fw6">FDA validator rules for SENDIG 3.1.1:</span> {ratioText(fda.mapped, fda.published)} name a
        rule this validator carries, and {fda.unmapped} don&apos;t. Naming a rule isn&apos;t running it: whether it ran
        is the published rules&apos; own count.
      </li>
    </ul>
  );
};

export default SendCapabilityLines;
